import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetAvailabilityScheduleService } from 'app/_services/asset-availability-schedule.service';
import { NotificationService } from 'app/_services/notification.service';
import { ScheduleValidationService } from 'app/_services/schedule-validation.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';

@Component({
  selector: 'add-availability-modal',
  templateUrl: './add-availability-modal.component.html'
})
export class AddAvailabilityModalComponent implements OnInit {
  public readonly directReasonMaxLength = 300;
  public readonly combinedReasonMaxLength = 300;

  @Input() assetId: any;
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() needsApproval: boolean;

  public form: FormGroup;
  public validationErrors: any[] = [];
  public minDate = new Date();
  public saveInProgress = false;
  public endTimeShowChangeOccurred = false;

  constructor(
    private activeModal: NgbActiveModal,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    private scheduleValidationService: ScheduleValidationService,
    private assetAvailabilityScheduleService: AssetAvailabilityScheduleService,
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit() {
    this.minDate.setUTCHours(0, 0, 0, 0);

    const reasonValidators = this.needsApproval ? [Validators.required] : [Validators.maxLength(this.directReasonMaxLength)];
    const workersValidators = this.needsApproval ? [Validators.required] : [];

    this.form = this.formBuilder.group({
      scheduledStartTime: new FormControl(this.startDate),
      scheduledEndTime: new FormControl(this.endDate),
      reason: new FormControl('', reasonValidators),
      workers: new FormControl('', workersValidators),
    }, { validator: this.combinedReasonLengthValidator() });
  }

  public get combinedReasonLength(): number {
    return this.buildSubmittedReason(this.form.value).length;
  }

  public closeModal() {
    this.activeModal.dismiss();
  }

  // Update the end time Date when the start time changes, for convenience
  public onStartTimeChange() {
    const start: Date = this.form.get('scheduledStartTime').value;
    const end: Date = this.form.get('scheduledEndTime').value;

    if (start && !end) {
      this.form.patchValue({ scheduledEndTime: start });
      this.animateEndTimeField();
    }

    if (start && end) {
      if (start.getDay() !== end.getDay()) {
        const newEnd = new Date(start.getTime());
        newEnd.setHours(end.getHours(), end.getMinutes());

        this.form.patchValue({ scheduledEndTime: newEnd });
        this.animateEndTimeField();
      }
    }
  }

  private animateEndTimeField() {
    this.endTimeShowChangeOccurred = true;
    setTimeout(() => {
      this.endTimeShowChangeOccurred = false;
    }, 250);
  }


  public onSubmit(ignoreOverlap = false) {
    this.saveInProgress = true;
    const values = this.form.value;

    this.validationErrors = [];
    const validation = this.scheduleValidationService.validateForm(values);

    if (!validation.isValid) {
      this.validationErrors = validation.validationErrors;
      this.saveInProgress = false;
      return
    }

    const body = {
      assetId: this.assetId,
      startTime: values.scheduledStartTime,
      endTime: values.scheduledEndTime,
      reason: values.reason,
    };

    if (this.needsApproval) {
      this.createPendingAvailabilityItem(values, ignoreOverlap);
    } else {
      this.createApprovedAvailabilityItem(body);
    }
  }

  private createPendingAvailabilityItem(values: any, ignoreOverlap: boolean) {
    let body = {
      workflowType: 'ASSET_BOOKING_REQUEST',
      assetId: this.assetId,
      startTime: values.scheduledStartTime,
      endTime: values.scheduledEndTime,
      reason: this.buildSubmittedReason(values),
      ignoreOverlap: ignoreOverlap,
    };

    this.workflowItemService.createWorkflowItem(body).subscribe(() => {
      this.notificationService.notify('success', 'Success', 'Request has been sent.');
      this.activeModal.close('refresh');
    }, error => {
      this.processError(error);
    });
  }

  private createApprovedAvailabilityItem(body: { assetId: any; startTime: any; endTime: any; reason: any; }) {
    this.assetAvailabilityScheduleService.createAvailabilityItem(body).subscribe(() => {
      this.notificationService.notify('success', 'Success', 'Availability window has been added.');
      this.activeModal.close('refresh');
    }, error => {
      this.processError(error);
    });
  }

  private processError(error: any) {
    let message = 'Schedule could not be saved';
    if (error && error.error) {
      if (error.error.title) {
        message = error.error.title;
      }
      if (error.error.fieldErrors && error.error.fieldErrors[0] && error.error.fieldErrors[0].message) {
        message = error.error.fieldErrors[0].message;
      }
    }

    this.notificationService.notify('danger', 'Error', message);
    this.saveInProgress = false;
  }

  private combinedReasonLengthValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      if (!this.needsApproval) {
        return null;
      }

      return this.buildSubmittedReason(formGroup.value).length > this.combinedReasonMaxLength ? { combinedReasonTooLong: true } : null;
    };
  }

  private buildSubmittedReason(values: { reason: string; workers: string; }): string {
    return values.reason + ' ' + values.workers;
  }
}
